import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import useAuthStore from '../store/authStore';
import configs from '../config';

const PropelLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className="w-10 h-10">
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export function MagicLinkPage() {
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { token, tenantSlug } = useParams();
  const { hasHydrated } = useAuthStore();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid sign-in link.');
      return;
    }

    const consumeToken = async () => {
      try {
        const { getTenantHeaders } = await import('../utils/apiClient');
        let tenantHeaders = {};

        if (tenantSlug) {
          tenantHeaders = { 'X-Tenant-Subdomain': tenantSlug.trim().toLowerCase() };
          try { localStorage.setItem('tenant-slug', tenantSlug.trim().toLowerCase()); } catch {}
        } else {
          tenantHeaders = getTenantHeaders();
          delete tenantHeaders['Content-Type'];
        }

        const response = await fetch(`${configs.BACKEND_URL}/api/user/magic-link/consume`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tenantHeaders,
          },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Invalid or expired sign-in link.');
        }

        const userData = await response.json();

        useAuthStore.setState({
          user: userData.user,
          userPermissions: userData.permissions || [],
          isAuthenticated: true,
        });

        setStatus('success');

        setTimeout(() => {
          if (userData.user?.role === 'owner') {
            navigate('/owner/tenants', { replace: true });
          } else if (userData.user?.role === 'admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }, 1500);
      } catch (err) {
        setStatus('error');
        setError(err.message || 'Something went wrong. Please request a new sign-in link.');
      }
    };

    consumeToken();
  }, [token, tenantSlug, navigate]);

  if (!hasHydrated) return null;

  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Link to="/" className="flex items-center space-x-2 mb-2">
            <PropelLogo />
          </Link>
          <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          {status === 'verifying' && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              <p className="text-sm text-gray-600">Verifying your sign-in link...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <p className="text-sm font-medium text-gray-900">Signed in successfully!</p>
              <p className="text-xs text-gray-500">Redirecting you now...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-4">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Sign-in failed</p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
              <Link
                to={loginPath}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Propel. All rights reserved.
        </p>
      </div>
    </div>
  );
}
